<script lang="ts">
  import Layout from "$app/Layouts/Admin.svelte"
  import Modal from "$app/components/Modal.svelte"
  import {
    ExternalLink,
    Pencil,
    Trash2,
    ShieldCheck,
    ShieldX
  } from "lucide-svelte"
  import { formatDate, toast, xhr, timeAgo, wait } from "$app/utils"
  import ChannelForm from "./ChannelForm.svelte"
  import PostForm from "./Channel/PostForm.svelte"
  import CommentForm from "./Channel/CommentForm.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    channels: ForumChannelType[]
    last_posts: ForumPostType[]
    last_comments: ForumPostCommentType[]
  }

  let props = $props()
  let channels = $state(props.channels)
  let last_posts = $state(props.last_posts)
  let last_comments = $state(props.last_comments)

  let openChannelFormModal = $state(false)
  let openPostFormModal = $state(false)
  let openCommentFormModal = $state(false)

  let currentChannel: ForumChannelType | {} = $state({} as ForumChannelType)
  let channelStatus = $state("")

  function editChannel(channel: ForumChannelType): void {
    currentChannel = channel
    channelStatus = "edit"
    openChannelFormModal = true
  }

  function updateChannel(channel: ForumChannelType): void {
    channelStatus = ""
    openChannelFormModal = false
    const idx = channels.findIndex((b) => b.id === channel.id)
    channels[idx] = channel
  }

  function addChannel(channel: ForumChannelType): void {
    channelStatus = ""
    openChannelFormModal = false
    channels = [channel, ...channels]
  }

  async function deleteChannel(channel: ForumChannelType): Promise<void> {
    channel._selected = true
    channels = channels
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected channel?")) {
      channel._selected = false
      channels = channels
      return
    }

    try {
      await xhr.delete(`/admin/community_channels/${channel.id}`)
      channels = channels.filter((b) => b.id !== channel.id)
      toast("Channel deleted successfully", "success")
    } catch (e) {
      toast("Error deleting channel", "error")
    } finally {
      channel._selected = false
      channels = channels
    }
  }

  let currentPost: ForumPostType | {} = $state({} as ForumPostType)
  let postStatus = $state("")

  function editPost(post: ForumPostType): void {
    currentPost = post
    postStatus = "edit"
    openPostFormModal = true
  }

  function updatePost(post: ForumPostType): void {
    postStatus = ""
    openPostFormModal = false
    const idx = last_posts.findIndex((b) => b.id === post.id)
    last_posts[idx] = post
    last_posts = last_posts
  }

  function createPost(post: ForumPostType): void {
    postStatus = ""
    openPostFormModal = false
    last_posts = [...last_posts, post]
  }

  async function deletePost(post: ForumPostType): Promise<void> {
    post._selected = true
    last_posts = last_posts
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected post?")) {
      post._selected = false
      last_posts = last_posts
      return
    }

    try {
      await xhr.delete(`/admin/community_posts/${post.id}`)
      last_posts = last_posts.filter((b) => b.id !== post.id)
      toast("Post deleted successfully", "success")
    } catch (e) {
      toast("Error deleting post", "error")
    } finally {
      post._selected = false
      last_posts = last_posts
    }
  }

  let currentComment: ForumPostCommentType | {} = $state(
    {} as ForumPostCommentType
  )
  let commentStatus = $state("")

  function editComment(comment: ForumPostCommentType): void {
    currentComment = comment
    commentStatus = "edit"
    openCommentFormModal = true
  }

  function updateComment(comment: ForumPostCommentType): void {
    commentStatus = ""
    openCommentFormModal = false
    const idx = last_posts.findIndex((b) => b.id === comment.id)
    last_comments[idx] = comment
    last_comments = last_comments
  }

  async function deleteComment(comment: ForumPostCommentType): Promise<void> {
    comment._selected = true
    last_comments = last_comments
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected comment?")) {
      comment._selected = false
      last_comments = last_comments
      return
    }

    try {
      await xhr.delete(`/admin/community_comments/${comment.id}`)
      last_comments = last_comments.filter((b) => b.id !== comment.id)
      toast("Comment deleted successfully", "success")
    } catch (e) {
      toast("Error deleting comment", "error")
    } finally {
      comment._selected = false
      last_comments = last_comments
    }
  }
</script>

<Layout {...props}>
  {#snippet title()}
    <span>Community Forum</span>
  {/snippet}

  <div class="p-8">
    <div class="flex justify-between">
      <div class="w-full text-right">
        <button
          class="btn primary font-bold text-lg"
          onclick={() => {
            currentChannel = {}
            channelStatus = "new"
            openChannelFormModal = true
          }}
        >
          + New Channel
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-4">
      <div class="border border-gray-500 bg-white rounded-lg p-6 shadow">
        <h2 class="text-base font-semibold leading-6 text-gray-900">
          Public Channels
        </h2>
        <div class="mt-4 overflow-y-auto h-500">
          <table class="min-w-full divide-y divide-gray-300">
            <thead class="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >Name</th
                >
                <th
                  class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                  >Post Count</th
                >
                <th
                  colspan="2"
                  class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                  >Actions</th
                >
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              {#each channels.filter( (channel) => ["public_channel", "broadcast_channel"].includes(channel.channel_type) ) as channel}
                <tr>
                  <td
                    class="whitespace-nowrap py-1 px-2 text-sm font-medium text-gray-900"
                    ><div class="flex items-center">
                      {channel.name}
                      {#if channel.enabled}
                        <span class="text-xs text-green-500 ml-2"
                          ><ShieldCheck class="ml-1 size-4" /></span
                        >
                      {:else}
                        <span class="text-xs text-red-500 ml-2"
                          ><ShieldX class="ml-1 size-4" /></span
                        >
                      {/if}
                    </div>
                  </td>
                  <td
                    class="whitespace-nowrap px-2 py-1 text-sm text-gray-500 text-center"
                    >{channel.post_count}</td
                  >
                  <td class="whitespace-nowrap px-2 py-1 text-sm text-gray-500">
                    <div class="flex justify-center gap-x-4 px-3 py-2">
                      <a href={`/admin/community_channels/${channel.id}`}>
                        <ExternalLink
                          class="w-6 h-6 text-gray-500 hover:text-blue-600"
                        />
                      </a>
                      <button onclick={() => editChannel(channel)}>
                        <Pencil
                          class="w-6 h-6 text-gray-500 hover:text-blue-500"
                        />
                      </button>
                      <button onclick={() => deleteChannel(channel)}>
                        <Trash2
                          class="w-6 h-6 text-gray-500 hover:text-red-500"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
      <div class="border border-gray-500 bg-white rounded-lg p-6 shadow">
        <h2 class="text-base font-semibold leading-6 text-gray-900">
          Private Channels
        </h2>
        <div class="mt-4 overflow-y-auto h-500">
          <table class="w-full data-table border-gray-600 mt-4 px-4 py-2">
            <thead class="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >Name</th
                >
                <th
                  class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center"
                  >Post Count</th
                >
                <th
                  class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center"
                  >User Count</th
                >
                <th
                  colspan="2"
                  class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                  >Actions</th
                >
              </tr>
            </thead>

            <tbody class="divide-y divide-gray-200 bg-white">
              {#each channels.filter((channel) => channel.channel_type === "private_channel") as channel}
                <tr>
                  <td
                    class="whitespace-nowrap py-1 px-2 text-sm font-medium text-gray-900"
                    ><div class="flex items-center">
                      {channel.name}
                      {#if channel.enabled}
                        <span class="text-xs text-green-500 ml-2"
                          ><ShieldCheck class="ml-1 size-4" /></span
                        >
                      {:else}
                        <span class="text-xs text-red-500 ml-2"
                          ><ShieldX class="ml-1 size-4" /></span
                        >
                      {/if}
                    </div></td
                  >
                  <td
                    class="whitespace-nowrap px-3 py-1 text-sm text-gray-500 text-center"
                    >{channel.post_count}</td
                  >
                  <td
                    class="whitespace-nowrap px-3 py-1 text-sm text-gray-500 text-center"
                  >
                    {channel.user_count}
                  </td>
                  <td class="whitespace-nowrap px-3 py-1 text-sm text-gray-500">
                    <div class="flex justify-center gap-4 px-4 py-2">
                      <a href={`/admin/community_channels/${channel.id}`}>
                        <ExternalLink
                          class="w-6 h-6 text-gray-500 hover:text-blue-600"
                        />
                      </a>
                      <button onclick={() => editChannel(channel)}>
                        <Pencil
                          class="w-6 h-6 text-gray-500 hover:text-blue-500"
                        />
                      </button>
                      <button onclick={() => deleteChannel(channel)}>
                        <Trash2
                          class="w-6 h-6 text-gray-500 hover:text-red-500"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="p-8 mt-4 border border-gray-500 rounded-lg shadow bg-white">
      <h2 class="text-base font-semibold leading-6 text-gray-900">
        Last 10 posts
      </h2>

      <div class="max-h-128 overflow-y-auto mt-4">
        <ul class="space-y-4">
          {#each last_posts as post}
            <li class="flex justify-between gap-4">
              <div
                class="bg-gray-50 border border-gray-100 rounded py-3 px-5 w-full shadow"
              >
                <div class="w-full flex justify-between">
                  <div class="flex space-x-3 py-4">
                    <div class="flex-shrink-0">
                      <img
                        class="h-10 w-10 rounded-xl shadow-lg"
                        src={post.user.avatar_thumb_url}
                        alt={post.user.name}
                      />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium text-gray-800">
                        {post.user.name}
                        <span class="ml-2 text-sm text-gray-600 font-medium"
                          >{timeAgo(post.created_at)}</span
                        >
                      </p>
                      <p class="text-sm text-gray-600">
                        {post.user.title}
                        <span class="text-xs ml-1">
                          {#if post.plan.company_name !== "null"}
                            @ {post.plan.company_name}
                          {:else}
                            @ {post.plan.name}
                          {/if}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div class="pt-4">
                    <p class="text-sm text-gray-700">
                      <span class="font-bold">Channel: </span>
                      {post.forum_channel.name}
                    </p>
                  </div>
                </div>
                <div class="p-4 border border-gray-200 rounded bg-white">
                  <div class="ProseMirror">
                    {@html post.content}
                  </div>
                </div>
              </div>
              <div class="pt-8">
                <p class="text-lg text-center text-white">Actions</p>
                <div class="flex justify-center gap-6 px-4 py-2">
                  <button onclick={() => editPost(post)}>
                    <Pencil class="w-6 h-6 text-gray-500 hover:text-blue-500" />
                  </button>
                  <button onclick={() => deletePost(post)}>
                    <Trash2 class="w-6 h-6 text-gray-500 hover:text-red-500" />
                  </button>
                </div>
              </div>
            </li>
          {/each}
        </ul>
      </div>
    </div>

    <div class="p-8 mt-4 border border-gray-500 rounded-lg shadow bg-white">
      <h2 class="text-base font-semibold leading-6 text-gray-900">
        Last 10 Comments
      </h2>

      <div class="max-h-128 overflow-y-auto mt-4">
        <ul class="space-y-3">
          {#each last_comments as comment}
            <li class="flex justify-between gap-4">
              <div class="bg-gray-50 rounded py-2 px-4 w-full">
                <div class="flex space-x-3 py-4 text-gray-700 items-center">
                  {comment.user_name}
                  <span class="text-gray-500 text-sm ml-2">
                    on {formatDate(comment.created_at)}</span
                  >
                </div>
                <div class="border p-4 rounded ProseMirror">
                  {@html comment.content}
                </div>
              </div>

              <div class="flex justify-center gap-6 px-4 py-2">
                <button onclick={() => editComment(comment)}>
                  <Pencil class="w-6 h-6 text-gray-500 hover:text-blue-500" />
                </button>
                <button onclick={() => deleteComment(comment)}>
                  <Trash2 class="w-6 h-6 text-gray-500 hover:text-red-500" />
                </button>
              </div>
            </li>
          {/each}
        </ul>
      </div>
    </div>
  </div>

  <Modal bind:open={openChannelFormModal} large={true}>
    <ChannelForm
      channel_id={currentChannel.id}
      status={channelStatus}
      onupdate={(channel) => updateChannel(channel)}
      oncreate={(channel) => addChannel(channel)}
    />
  </Modal>

  <Modal bind:open={openPostFormModal} large={true}>
    <PostForm
      post_id={currentPost.id}
      {channels}
      status={postStatus}
      onupdate={(post) => updatePost(post)}
      oncreate={(post) => createPost(post)}
    />
  </Modal>

  <Modal bind:open={openCommentFormModal} large={true}>
    <CommentForm
      comment_id={currentComment.id}
      open={commentStatus !== ""}
      status={commentStatus}
      onupdate={(comment) => updateComment(comment)}
    />
  </Modal>
</Layout>
